import { useId, useState } from "react";
import { describedBy, FieldMessage } from "./Field.tsx";
import { CheckIcon, UploadIcon } from "./Icons.tsx";

/**
 * A file picker: a button that opens the system file chooser, and the name of the chosen file
 * beside it.
 *
 * The native file input is ugly and says "No file chosen" in the browser's own words, so it is
 * hidden (still in the page, still reachable by keyboard) and a label drawn like a secondary button
 * opens it. The focus ring moves to that button.
 *
 * The input is emptied after each pick, so choosing the same file again (after fixing it) reads it
 * again.
 *
 * With `multiple` the chooser takes several files at once and `onFiles` gets all of them, in the
 * order the chooser gives.
 */
export function FileField(props: FileFieldProps) {
  const {
    label,
    hint,
    error,
    accept,
    buttonLabel = "Choose a file",
    fileName,
    disabled = false,
    hideLabel = false,
    showFileName = true,
    fullWidth = false,
    className = "",
  } = props;
  const id = useId();
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const shown = showFileName ? (fileName ?? picked) : undefined;

  return (
    <div className={["flex flex-col gap-1.5", className].filter(Boolean).join(" ")}>
      <label id={`${id}-label`} htmlFor={id} className={hideLabel ? "sr-only" : "eyebrow text-ink-2"}>
        {label}
      </label>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <input
          id={id}
          type="file"
          accept={accept}
          multiple={props.multiple}
          disabled={disabled}
          // Named by the field label only. The button below is a second label (so a click on it
          // opens the chooser), and without this its words would be added to the name.
          aria-labelledby={`${id}-label`}
          aria-describedby={describedBy(id, hint, error)}
          aria-invalid={error ? true : undefined}
          className="peer sr-only"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (files.length === 0) return;
            setPicked(
              files.length === 1 ? files[0]!.name : `${files.length} files: ${files.map((file) => file.name).join(", ")}`,
            );
            if (props.multiple) props.onFiles(files);
            else props.onFile(files[0]!);
          }}
        />
        <label
          htmlFor={id}
          aria-hidden="true"
          className={[
            "sk-btn-alt inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 text-[15px] font-medium",
            fullWidth ? "w-full" : "",
            "peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-soft",
            "peer-disabled:cursor-not-allowed peer-disabled:opacity-45",
            error ? "border-cancelled" : "border-transparent",
          ].join(" ")}
        >
          <UploadIcon size={18} className="text-ink-2" />
          {buttonLabel}
        </label>
        {/* Always in the page, so a screen reader hears the name when it appears. */}
        <span className="flex min-w-0 items-center gap-1.5 text-[14px] text-ink" aria-live="polite">
          {shown ? (
            <>
              {/* No tick when the file is wrong: the error under the field says why. */}
              {error ? null : <CheckIcon size={16} className="shrink-0 text-confirmed" />}
              <span className="truncate">{shown}</span>
            </>
          ) : null}
        </span>
      </div>
      <FieldMessage id={id} hint={hint} error={error} />
    </div>
  );
}

export type FileFieldProps = {
  label: string;
  hint?: string;
  error?: string;
  /** The file types the chooser offers: ".csv,text/csv". */
  accept?: string;
  /** The words on the button: "Choose a .csv file", or "Reading the file" while busy. */
  buttonLabel?: string;
  /** The chosen file's name, when the screen keeps it. Else the field shows the last pick. */
  fileName?: string;
  disabled?: boolean;
  /** Keep the label for screen readers only, when a heading beside the field already says it. */
  hideLabel?: boolean;
  /** False when the screen already says which file it read (with its row count, for example). */
  showFileName?: boolean;
  /** The button fills the width, like the other actions in a card. */
  fullWidth?: boolean;
  /** Places the field in its parent (`mt-auto`). */
  className?: string;
} & (FileFieldSingle | { multiple: true; onFiles: (files: File[]) => void });

/** The one-file form, the default. */
export type FileFieldSingle = { multiple?: false; onFile: (file: File) => void };
